"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";

interface NavWrapperProps {
	children: React.ReactNode;
}

const SCROLL_THRESHOLD_Y = 50;
/** Ignore tiny scroll jitter when deciding direction */
const DIRECTION_EPSILON = 16;
/** Hide sticky glass this long after the user scrolls down (scroll-up keeps the bar until then). */
const STICKY_HIDE_AFTER_SCROLL_DOWN_MS = 3000;
/** After auto-hide, ignore weak “scroll up” events from layout reflow (ms) */
const POST_HIDE_SCROLL_COOLDOWN_MS = 220;
/** Minimum upward delta to count as scroll-up during cooldown */
const COOLDOWN_BREAK_SCROLL_UP = -28;

export default function NavWrapper({ children }: NavWrapperProps) {
	const [showStickyGlass, setShowStickyGlass] = useState(false);
	const [pastHero, setPastHero] = useState(false);
	const [navFullyHidden, setNavFullyHidden] = useState(false);
	const [mounted, setMounted] = useState(false);
	const pathname = usePathname();

	const lastScrollYRef = useRef(0);
	const dismissedUntilScrollUpRef = useRef(false);
	const stickyVisibleRef = useRef(false);
	const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pastHeroRef = useRef(false);
	const rafRef = useRef<number>(0);
	const ignoreWeakScrollUpUntilRef = useRef(0);
	const navInteractionRef = useRef(false);

	const clearHideTimer = () => {
		if (hideTimerRef.current != null) {
			clearTimeout(hideTimerRef.current);
			hideTimerRef.current = null;
		}
	};

	const setStickyVisible = (next: boolean) => {
		stickyVisibleRef.current = next;
		setShowStickyGlass(next);
	};

	const armHideTimer = () => {
		clearHideTimer();
		hideTimerRef.current = setTimeout(() => {
			if (navInteractionRef.current) {
				hideTimerRef.current = null;
				return;
			}
			setStickyVisible(false);
			dismissedUntilScrollUpRef.current = true;
			setNavFullyHidden(true);
			ignoreWeakScrollUpUntilRef.current = Date.now() + POST_HIDE_SCROLL_COOLDOWN_MS;
			hideTimerRef.current = null;
		}, STICKY_HIDE_AFTER_SCROLL_DOWN_MS);
	};

	useEffect(() => {
		setMounted(true);
		if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
			window.history.scrollRestoration = "manual";
		}
		const y0 = window.scrollY;
		lastScrollYRef.current = y0;
		const initialPast = y0 > SCROLL_THRESHOLD_Y;
		pastHeroRef.current = initialPast;
		setPastHero(initialPast);
		if (y0 > SCROLL_THRESHOLD_Y) {
			dismissedUntilScrollUpRef.current = true;
			setNavFullyHidden(true);
		}

		const runScrollLogic = () => {
			const y = window.scrollY;
			const lastY = lastScrollYRef.current;
			const delta = y - lastY;

			if (y <= SCROLL_THRESHOLD_Y) {
				clearHideTimer();
				dismissedUntilScrollUpRef.current = false;
				setNavFullyHidden(false);
				ignoreWeakScrollUpUntilRef.current = 0;
				setStickyVisible(false);
				if (pastHeroRef.current) {
					pastHeroRef.current = false;
					setPastHero(false);
				}
				lastScrollYRef.current = y;
				return;
			}

			/* First crossing below hero: show glass even if scroll delta is tiny (avoids stuck transparent bar). */
			if (!pastHeroRef.current) {
				pastHeroRef.current = true;
				setPastHero(true);
				if (!dismissedUntilScrollUpRef.current && !stickyVisibleRef.current) {
					setNavFullyHidden(false);
					setStickyVisible(true);
					armHideTimer();
				}
				lastScrollYRef.current = y;
				return;
			}

			lastScrollYRef.current = y;

			const goingUp = delta < 0;
			const goingDown = delta > DIRECTION_EPSILON;

			if (goingUp) {
				const now = Date.now();
				if (
					now < ignoreWeakScrollUpUntilRef.current &&
					delta > COOLDOWN_BREAK_SCROLL_UP
				) {
					return;
				}

				dismissedUntilScrollUpRef.current = false;
				setNavFullyHidden(false);
				if (!stickyVisibleRef.current) {
					setStickyVisible(true);
				}
				/* Stay visible until the user scrolls down again (then 3s debounced hide). */
				clearHideTimer();
				return;
			}

			if (goingDown) {
				if (navInteractionRef.current) {
					clearHideTimer();
					return;
				}

				/* Scrolling down while the bar is shown: arm / refresh hide-after-idle timer. */
				if (stickyVisibleRef.current) {
					armHideTimer();
				} else if (!dismissedUntilScrollUpRef.current) {
					setNavFullyHidden(false);
					setStickyVisible(true);
					armHideTimer();
				}
			}
		};

		const handleScroll = () => {
			if (rafRef.current) return;
			rafRef.current = window.requestAnimationFrame(() => {
				rafRef.current = 0;
				runScrollLogic();
			});
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		runScrollLogic();

		return () => {
			window.removeEventListener("scroll", handleScroll);
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = 0;
			}
			clearHideTimer();
		};
	}, []);

	useLayoutEffect(() => {
		if (typeof window === "undefined") return;

		const hasHash = window.location.hash.length > 0;
		if (!hasHash) {
			window.scrollTo(0, 0);
		}

		clearHideTimer();
		dismissedUntilScrollUpRef.current = false;
		stickyVisibleRef.current = false;
		setShowStickyGlass(false);
		setNavFullyHidden(false);
		ignoreWeakScrollUpUntilRef.current = 0;
		const y = hasHash ? window.scrollY : 0;
		lastScrollYRef.current = y;
		const initialPast = y > SCROLL_THRESHOLD_Y;
		pastHeroRef.current = initialPast;
		setPastHero(initialPast);
		if (initialPast) {
			dismissedUntilScrollUpRef.current = true;
			setNavFullyHidden(true);
		}
	}, [pathname]);

	const shouldShowScrollStyles = mounted && showStickyGlass;
	const navObscured = mounted && pastHero && !showStickyGlass && navFullyHidden;

	const showNavWhileInteracting = () => {
		navInteractionRef.current = true;
		clearHideTimer();
		if (pastHeroRef.current) {
			dismissedUntilScrollUpRef.current = false;
			setNavFullyHidden(false);
			setStickyVisible(true);
		}
	};

	const endNavInteraction = () => {
		navInteractionRef.current = false;
	};

	const isMovingWithinNav = (currentTarget: EventTarget & HTMLDivElement, relatedTarget: EventTarget | null) => {
		return relatedTarget instanceof Node && currentTarget.contains(relatedTarget);
	};

	return (
		<div
			data-site-header
			onMouseEnter={showNavWhileInteracting}
			onMouseLeave={(event) => {
				if (isMovingWithinNav(event.currentTarget, event.relatedTarget)) return;
				endNavInteraction();
			}}
			onFocus={showNavWhileInteracting}
			onBlur={(event) => {
				if (isMovingWithinNav(event.currentTarget, event.relatedTarget)) return;
				endNavInteraction();
			}}
			className={cn(
				"sticky z-50 ease-out",
				pastHero ? "top-2 sm:top-0" : "top-0",
				/* Keep desktop breathing room, but do not create an empty strip above sticky mobile nav. */
				pastHero && "pt-0 sm:pt-6",
				/* Only animate opacity/visibility — not layout-affecting properties */
				"transition-[opacity,visibility] duration-300",
				!pastHero && "!bg-transparent",
				navObscured && "pointer-events-none invisible opacity-0",
				!navObscured && pastHero && !shouldShowScrollStyles && "!bg-transparent",
			)}
			data-nav-scrolled={String(!shouldShowScrollStyles)}
			data-nav-obscured={String(navObscured)}
			data-nav-sticky={String(shouldShowScrollStyles)}
			data-nav-past-hero={String(pastHero)}
			style={
				!shouldShowScrollStyles && !navObscured && !pastHero
					? {
							background: "transparent !important",
							backgroundColor: "transparent !important",
						}
					: undefined
			}
		>
			<nav
				className={cn(
					"relative isolate transition-[box-shadow,border-color,background-color] duration-300 ease-out",
					shouldShowScrollStyles
						? "mx-auto max-h-[76px] max-w-[88%] rounded-full bg-transparent sm:max-h-[90px]"
						: "inset-x-0 max-h-[90px] !bg-transparent",
				)}
				data-nav-scrolled={String(!shouldShowScrollStyles)}
				style={{
					background: "transparent",
					backgroundColor: "transparent",
				}}
			>
				{shouldShowScrollStyles ? (
					<span
						aria-hidden
						className="pointer-events-none absolute inset-0 z-0 rounded-full"
						style={{
							background: "var(--glass-background)",
							boxShadow: "var(--glass-shadow)",
							border: "1px solid var(--glass-border)",
							backdropFilter: "var(--glass-backdrop-filter)",
							WebkitBackdropFilter: "var(--glass-backdrop-filter)",
						}}
					/>
				) : null}
				<div
					className={cn(
						"container relative z-10 flex justify-between transition-all duration-300 md:justify-start",
						shouldShowScrollStyles ? "h-[76px] px-5 sm:h-[90px] sm:px-6" : "h-[90px] !bg-transparent",
					)}
					style={
						!shouldShowScrollStyles
							? {
									background: "transparent !important",
									backgroundColor: "transparent !important",
								}
							: undefined
					}
				>
					{children}
				</div>
			</nav>
		</div>
	);
}
